/**
 * Navigation
 *
 * It pulls all categories from an API. The API returns all categories as
 * strings. The string contains the HTML markup, which is assembled in the file
 * app/b2b/templates/javascript-content/nav-main-dynamic(-mobile).html Then the
 * string is parsed to an array of HTML objects.
 *
 * The script then searches for elements that have the class .js-nav-item.
 * These are normally the mega-dropdowns. Within the dropdowns
 * e.g. app/b2b/templates/modules/navigation/nav-category-20.html the script
 * then searches for a <li> element with the following attributes:
 *
 * |       attribute        |                                                                         description                                                                               |            value             |
 * |------------------------|-------------------------------------------------------------------------------------------------------------------------------------------------------------------|------------------------------|
 * | class                  | selector for identifying the element                                                                                                                              | js-nav-load-category         |
 * | data-load-category     | category label of the category to be loaded                                                                                                                       | category label (xx.xx.xx.xx) |
 * | data-nav-no-parents    | should parent categories be displayed? This applies to the parent category of the category to be loaded as well as to all parent categories within this category. | bool (default: false)        |
 * | data-nav-hide-title    | hide the title/ parent category element to show only subcategories                                                                                                | bool (default: false)        |
 *
 * Example: <li class="js-nav-load-category" data-load-category="20.00.00.00" data-nav-no-parents="true"></li>
 *
 * Here the category 20.00.00.00 is loaded. The data-attribute
 * data-nav-no-parents hides the element of the parent category (20.00.00.00)
 * via the class 'hidden' and all parent categories within the subcategories
 * are actually not inserted into the element. This corresponds to the current
 * requirement of mega-dropdowns.
 *
 * The resulting output looks like this:
 *	<li class="js-nav-load-category" data-load-category="20.00.00.00" data-nav-no-parents="true">
 *		<h2 class="h4 arrow-bold hidden">
 *			<a href="/catalogue/lingerie and fashion">
 *				<span class="nav-category-text">Dessous &amp; Fashion</span>
 *			</a>
 *		</h2>
 *		<ul>
 *			<li data-parent-category="20.00.00.00" data-category="20.12.00.00">
 *				<a href="/catalogue/lingerie-and-fashion/high-heels"> <span class="nav-category-text">high heels</span></a>
 *			</li>
 *			...
 *		</ul>
 *	</li>
 *
 * Important: The JavascriptCategory controller generates the out with the "navigation" mode. This
 * controllers adds a specific caching header to prevent the whole navigation structure being loaded
 * for each single request. Thus we add the shopProfileCode (authorized = customer logged in /
 * default = customer not logged in) and the current software revison to the request. This
 * leads to separate caches e.g. for the navigation in guest and logged in view.
 */
class Navigation
{
    /**
	 * Navigation constructor
	 */
    constructor()
    {
        /**
		 * @type {string}
		 */
        this.navItemSelector = '.js-nav-item';

        /**
		 * @type {string}
		 */
        this.labelAttributeIndex = 'categoryLabel';

        /**
		 * @type {object}
		 */
        this.categoryLabelRegex = /(\d\d\.){3}\d\d/;

        /**
		 * @type {object[]}
		 */
        this.navItems = [];

        /**
		 * @type {object[]}
		 */
        this.categoryList = [];
    }

    /**
	 * Initialize the navigation
	 */
    init()
    {
        let navItems = document.querySelectorAll(this.navItemSelector);
        this.navItems = Array.from(navItems); // Convert NodeList to Array because of browser compatibility
        this.isCategoriesLoaded = false;
        this.categoryList = [];

        this.fetchCategoryList().done(categoryList =>
        {
            let categoryNodeList = new DOMParser().parseFromString(categoryList, 'text/html').body.childNodes;
            let categoryArray = Array.from(categoryNodeList);

            this.categoryList = categoryArray;
            this.isCategoriesLoaded = true;

            this.navItems.forEach(function(item)
            {
                this.processNavContent(item);
            }.bind(this));
        });
    }


    /**
	 * Returns a promise of the request to fetch the categories
	 * @return {jqXHR} promise of the navigation content request
	 */
    fetchCategoryList()
    {
        // beware, dirty stuff. We need the revision id here, but theres no
        // possibility to get in javascript atm so we're fetching it from
        // another asset.
        let query = '?';
        let assetElement = document.querySelector('link[href*=kendo]');

        if (assetElement !== null)
        {
            // Get the last 6 characters of the href attribute value (url)
            query += 'id=' + assetElement.getAttribute('href').substr(-6, 6);
        }

        query += '&shopProfileCode=' + orion.getShopProfileCode();

        let promise = $.ajax({
            'url': '/js_catalog/00.00.00.00/navigation' + query,
            'method': 'GET',
            'cache': true
        });

        return promise;
    }

    /**
	 * Filters an array including all categories as <li> elements and returns
	 * just an array of categories with the given category label.
	 * @param {object[]} allCategories - An array including all (sub-)categories
	 * @param {string} categoryLabel - Category label (xx.xx.xx.xx) of the category
	 * @return {object[]} The category of the given category
	 */
    getCategoryElementByCategoryLabel(allCategories, categoryLabel)
    {
        let categoryElement = allCategories.filter(categoryElement =>
        {
            let { tagName } = categoryElement;
            let currentCategoryLabel = '';

            if (tagName === 'LI')
            {
                currentCategoryLabel = this.fetchCategoryLabel(categoryElement);
            }

            return currentCategoryLabel === categoryLabel;
        });

        if (!categoryElement.length)
        {
            return [];
        }

        return categoryElement;
    }

    /**
	 * Filters an array including all categories as <li> elements and returns
	 * all subcategories of the given parent category label.
	 * @param {object[]} allCategories - An array including all (sub-)categories
	 * @param {string} parentCategoryLabel - Category label (xx.xx.xx.xx) of the parent category
	 * @param {bool} noParents - Remove or keep categories with subcategories
	 * @return {object[]} All subcategories of the given parent category
	 */
    getSubcategoriesByParentCategoryLabel(allCategories, parentCategoryLabel, noParents = false)
    {
        let subcategories = allCategories.filter(categoryElement =>
        {
            let { tagName } = categoryElement;
            let currentParentCategoryLabel = '';

            if (tagName === 'LI')
            {
                currentParentCategoryLabel = this.fetchParentCategoryLabel(categoryElement);
            }

            return currentParentCategoryLabel === parentCategoryLabel;
        });

        if (subcategories.length && noParents === true)
        {
            subcategories = subcategories.filter(subcategoryElement =>
            {
                let categoryLabel = this.fetchCategoryLabel(subcategoryElement);

                return !this.hasSubcategories(allCategories, categoryLabel);
            });
        }

        if (!subcategories.length)
        {
            return false;
        }

        return subcategories;
    }

    /**
	 * Checks if the given category has subcategories by searching through all categories
	 * @param {object[]} allCategories - An array of all categories
	 * @param {string} categoryLabel - The category label to check for subcategories
	 * @return {bool}
	 */
    hasSubcategories(allCategories, categoryLabel)
    {
        let subcategories = allCategories.filter(categoryElement =>
        {
            let { tagName } = categoryElement;

            if (tagName !== 'LI')
            {
                return false;
            }

            return categoryElement.dataset.parentCategory === categoryLabel;
        });

        return subcategories.length > 0;
    }

    /**
	 * Transforms the <li> elements to include the parent category subcategories in the corresponding dropdown.
	 * @param {object} navItem - The navigation item where to look for the <ul> elements
	 */
    processNavContent(navItem)
    {
        let dropdownTarget = this.fetchDropdownTarget(navItem);
        let dropdown = document.querySelector('#' + dropdownTarget);

        if (dropdown === null)
        {
            return;
        }

        let subcategoryElements = dropdown.querySelectorAll('.js-nav-load-category');
        let subcategoryElementsArray = Array.from(subcategoryElements);

        if (!subcategoryElements.length)
        {
            return;
        }

        subcategoryElementsArray.forEach(function(subcategoryElement)
        {
            let parentCategoryLabel = subcategoryElement.dataset.loadCategory;
            let noParents = typeof subcategoryElement.dataset.navNoParents !== 'undefined';
            let hideTitle = typeof subcategoryElement.dataset.navHideTitle !== 'undefined';

            let subcategories =
				this.getSubcategoriesByParentCategoryLabel(this.categoryList, parentCategoryLabel, noParents);

            if (!subcategories)
            {
                return;
            }

            let parentCategoryElement =
				this.getCategoryElementByCategoryLabel(this.categoryList, parentCategoryLabel).shift();

            if (typeof parentCategoryElement !== 'undefined')
            {
                let parentCategoryLinkElement = parentCategoryElement.querySelector('a');
                let titleElement = this.createTitleElement();

                if (noParents || hideTitle)
                {
                    titleElement.classList.add('hidden');
                }

                titleElement.appendChild(parentCategoryLinkElement);

                subcategoryElement.innerHTML = titleElement.outerHTML;
            }


            let subcategoriesWrapperElement = this.createSubcategoriesWrapperElement();

            subcategories.forEach(subcategory =>
            {
                subcategoriesWrapperElement.appendChild(subcategory);
            });

            subcategoryElement.appendChild(subcategoriesWrapperElement);
        }.bind(this));
    }

    /**
	 * Fetchs and validates the value of the data-category-label (xx.xx.xx.xx) attribute
	 * @param {object} element - The element with the data-category-label attribute
	 * @return {string} The category label
	 */
    fetchCategoryLabel(element)
    {
        let categoryLabel = element.dataset.category;

        if (typeof categoryLabel === 'undefined' || !categoryLabel.match(this.categoryLabelRegex))
        {
            return false;
        }

        return categoryLabel;
    }

    /**
	 * Fetchs and validates the value of the data-parent-category (xx.xx.xx.xx) attribute
	 * @param {object} element - The element with the data-parent-category attribute
	 * @return {string} The parent category label
	 */
    fetchParentCategoryLabel(element)
    {
        let parentCategoryLabel = element.dataset.parentCategory;

        if (typeof parentCategoryLabel === 'undefined' || !parentCategoryLabel.match(this.categoryLabelRegex))
        {
            return false;
        }

        return parentCategoryLabel;
    }

    /**
	 * Fetchs the navigation items data-target attribut value (data-target), which is also used by dropdown.js
	 * @param {object} navItem - The navigation item with the data-target attribut
	 * @return {string} The data-target value. Used as selector for the target dropdown id (without #)
	 */
    fetchDropdownTarget(navItem)
    {
        let dropdownTarget = navItem.dataset.target;

        if (typeof dropdownTarget === 'undefined')
        {
            return false;
        }

        return dropdownTarget;
    }

    /**
	 * Creates the <h2> which wraps around the parent category <li> content
	 * @return {object} The <h2> HTML object
	 */
    createTitleElement()
    {
        let element = document.createElement('h2');
        element.classList.add('h5', 'arrow-bold');

        return element;
    }

    /**
	 * Creates the <ul> which wraps around the subcategories
	 * @return {object} The <ul> HTML object
	 */
    createSubcategoriesWrapperElement()
    {
        let element = document.createElement('ul');

        return element;
    }
}

let nav = new Navigation();
nav.init();
